import { useEffect, useState, type ReactNode } from 'react';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';
import { Modal, type ModalSize } from './Modal.js';

/**
 * Uma ação sobre um registro — o que o painel de reticências oferece.
 *
 * ## `run` OU `form`, nunca os dois
 *
 * São os dois desfechos possíveis de uma ação, e a união os torna exclusivos no
 * tipo em vez de numa frase de docblock:
 *
 * - **`run`** faz e acabou — aderir, arquivar, copiar, navegar. O painel fecha
 *   e a ação roda.
 * - **`form`** precisa perguntar alguma coisa antes — encaminhar pede
 *   departamento e justificativa. O painel **troca de conteúdo no lugar**, com
 *   uma seta de voltar, e o formulário aparece dentro do mesmo diálogo.
 *
 * A troca no lugar é o que evita o defeito que este componente existe para não
 * ter: um modal abrindo outro modal. Dois véus empilhados prendem o foco no
 * primeiro, e o segundo herda um fundo que já estava escurecido.
 *
 * ## `disabled` devolve o MOTIVO
 *
 * Nunca `true`. Ação cinzenta sem motivo escrito é a tela sabendo por que não
 * pode e não contando — quem lê conclui que o sistema está quebrado, tenta de
 * novo, e abre chamado. É também o que esta peça ganha sobre a coluna de
 * ícones que ela substitui: lá o botão simplesmente **não era renderizado**
 * quando a ação não cabia, e nada dizia por quê.
 */
interface ActionFields {
  label: string;
  icon?: IconName;
  /** `perigo` para o que não tem volta. */
  tone?: 'neutro' | 'perigo';
  /** O motivo de não poder, ou `false`. */
  disabled?: string | false;
}

export type Action = ActionFields &
  (
    | { run: () => void; form?: never; formSize?: never }
    | {
        /**
         * O formulário que ocupa o painel. `close` fecha o diálogo inteiro —
         * é o que o formulário chama quando termina de gravar.
         */
        form: (close: () => void) => ReactNode;
        /**
         * A largura do diálogo enquanto o formulário o ocupa — `md` quando
         * omitida. Existe para o formulário que é uma TABELA de campos (os
         * serviços do prestador da Assistência 24h, 02/11/2026): em 32rem
         * cada linha virava uma pilha de cinco campos.
         */
        formSize?: ModalSize;
        run?: never;
      }
  );

/**
 * O painel de ações de um registro — o desenho de 17/09.
 *
 * ## Por que ele não é um menu suspenso
 *
 * O desenho pede um diálogo centrado, e há razão além do gosto: a coluna de
 * ações é a última da tabela, e a tabela **rola de lado**. Um menu ancorado ao
 * gatilho nasce fora da vista no telefone, ou é reposicionado para um canto que
 * não tem relação com o que foi clicado. O diálogo centrado não depende de onde
 * o gatilho estava.
 *
 * E ele **nomeia o registro** no cabeçalho ("Item #17089"): a linha que
 * originou o clique some atrás do véu, e sem o nome a pessoa age sobre um
 * registro que ela está lembrando, não lendo.
 *
 * ## Ele não tem estado "carregando", e isso é construção
 *
 * O painel fecha ANTES de a ação rodar, então não há o que carregar dentro
 * dele: o progresso aparece onde o resultado aparece — a linha da tabela, ou o
 * aviso da tela. A ação que precisa esperar é a que tem `form`, e ali quem
 * mostra o progresso é o `Button` do próprio formulário. Declarado em
 * `statuses.test.ts`, que aceita a ausência só com o motivo escrito.
 *
 * ## Uma ação por linha, sempre — e o contrário está medido
 *
 * Até 21/09 a grade era `grid-cols-1 sm:grid-cols-2`, e as duas colunas nunca
 * foram desenho: o `sm:` do Tailwind mede a **janela**, e este diálogo é
 * `size="sm"` por construção — 24rem, 384px, em qualquer largura de tela. A
 * régua responsiva existia; ela só não media o contêiner.
 *
 * O que sobrava para o rótulo, com duas colunas: **88px**. São 352 de corpo,
 * menos 8 de respiro entre as colunas, menos 28 de recuo, menos 28 do ladrilho
 * do ícone e menos 28 do da seta, dividido por dois. "Copiar link de
 * acompanhamento" ocupa quatro linhas em 88px; "Aderir a mim", que é a ação
 * vizinha no MESMO painel, ocupa uma. Daí sai a lista inteira de defeitos —
 * altura desigual entre vizinhas, coluna que muda de largura conforme o
 * estado, e o painel de uma ação só deixando metade do diálogo vazia.
 *
 * É a lição que o `FormRow` já tinha aprendido: duas colunas iguais porque o
 * Tailwind as torna baratas, não porque os dois conteúdos tenham a mesma
 * largura (`DESIGN.md`, "a grade do formulário é de doze").
 *
 * E o caso que as duas colunas defendiam **não existe no produto**. Os painéis
 * mais ricos têm QUATRO ações — a mesa de demandas e a tela de usuários —, e
 * nos dois três das quatro carregam um `disabled`, que é uma frase inteira e
 * não uma palavra. Quatro linhas empilhadas somam ~250px; com o cabeçalho o
 * diálogo fica abaixo de 400 e não rola em tela nenhuma. A única coisa que as
 * duas colunas encurtavam era uma lista que o produto não produz.
 */
export function ActionsModal({
  open,
  onOpenChange,
  title,
  descricao,
  icon = 'quadro',
  actions,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** O nome do registro: "Demanda EV-QDJV". */
  title: string;
  /** A segunda linha do cabeçalho: "ID: 17089". */
  descricao?: string;
  icon?: IconName;
  actions: Action[];
}) {
  /* Qual ação tomou o painel. `null` é a grade. */
  const [active, setActive] = useState<Action | null>(null);

  /*
   * Fechar o painel devolve a grade.
   *
   * Sem isto, reabrir o painel de OUTRA linha traria o formulário da anterior
   * já aberto — e preenchido com o registro errado, porque o `form` foi criado
   * com o item de antes. É o tipo de defeito que só aparece na segunda
   * abertura, que é depois de todo teste de caminho feliz.
   */
  useEffect(() => {
    if (!open) setActive(null);
  }, [open]);

  const close = () => onOpenChange(false);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      tone="destaque"
      /*
       * No formulário o ladrilho CEDE o lugar para a seta de voltar.
       *
       * Com os dois, o cabeçalho ficava com duas pastilhas redondas lado a
       * lado — e a segunda era o ícone da própria ação, que em "Encaminhar" é
       * uma seta para a direita. Voltar encostado em avançar, os dois do mesmo
       * tamanho e na mesma cor: o desenho dizia que havia dois caminhos, e só
       * o da esquerda era clicável.
       */
      {...(active ? {} : { icon })}
      size={active ? (active.formSize ?? 'md') : 'sm'}
      title={active ? active.label : title}
      descricao={active ? title : descricao}
      leading={
        active && (
          <button
            type="button"
            onClick={() => setActive(null)}
            aria-label="Voltar para as ações"
            className="inline-flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-borda bg-superficie text-texto-suave transition-colors hover:text-texto active:scale-[0.94] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco pointer-coarse:size-11"
          >
            <Icon nome="seta-esquerda" size={20} />
          </button>
        )
      }
    >
      {active?.form ? (
        active.form(close)
      ) : (
        /* Pilha, e não grade: ver "Uma ação por linha" no docblock acima. */
        <ul className="flex flex-col gap-2">
          {actions.map((action) => (
            <li key={action.label}>
              <ActionButton
                action={action}
                onChoose={() => {
                  if (action.form) setActive(action);
                  else {
                    /* fecha ANTES de rodar: a ação pode navegar, e um diálogo
                       aberto durante a troca de rota deixa o véu na tela nova */
                    close();
                    action.run?.();
                  }
                }}
              />
            </li>
          ))}
        </ul>
      )}
    </Modal>
  );
}

/**
 * A anatomia é a MESMA nos dois estados, e é o conserto de 21/09.
 *
 * Três faixas: um ladrilho de 16px para o ícone, o rótulo que estica, e outro
 * ladrilho de 16px para a seta. Os dois das pontas existem mesmo quando não há
 * o que pôr dentro — a seta desabilitada fica `invisible`, e não ausente.
 * Antes ela era removida do DOM, e com isso o rótulo GANHAVA 28px ao
 * desabilitar: a mesma frase quebrava em lugares diferentes conforme o estado,
 * e duas ações vizinhas nunca alinhavam.
 *
 * `items-start`, com os ladrilhos na altura da caixa de linha do rótulo
 * (`size-4`, que é o que o `text-xs` produz), prende ícone e seta à PRIMEIRA
 * linha. Com `items-center` um motivo de três linhas jogava o ícone para o
 * meio do parágrafo, e a ação de uma linha ao lado dele parecia de outra peça.
 *
 * `py-3.5` dá 44px com uma linha — o piso de dedo que o `DESIGN.md` promete e
 * que o `py-3` de antes entregava por 40. O `min-h-11` ao lado declara o piso:
 * ele não dispara hoje, e dispara no dia em que a caixa de linha mudar.
 *
 * ## Desabilitado não é opacidade
 *
 * Era `disabled:opacity-60` no botão inteiro, e o filtro apagava junto o
 * MOTIVO — o único texto que a ação desabilitada existe para entregar.
 * Opacidade também mente sobre o contraste: `theme.test.ts` mede `texto-suave`
 * sobre `superficie-suave` em 4,5, e nenhuma dessas medições sobrevive a um
 * véu de 60% por cima. O estado passou a ser dito pela cor do rótulo, pelo
 * cursor e pela seta que some, e o motivo fica na cor que foi medida.
 */
function ActionButton({ action, onChoose }: { action: Action; onChoose: () => void }) {
  const { disabled, tone, icon, label } = action;
  const perigo = tone === 'perigo';

  return (
    <button
      type="button"
      disabled={Boolean(disabled)}
      onClick={onChoose}
      className={cn(
        'flex min-h-11 w-full items-start gap-3 rounded-borda bg-superficie-suave px-3.5 py-3.5 text-left',
        'peso-controle text-xs transition-colors active:scale-[0.98]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        'cursor-pointer disabled:cursor-not-allowed',
        disabled
          ? 'text-texto-suave'
          : perigo
            ? 'text-erro hover:bg-erro-suave'
            : 'text-texto hover:bg-primaria-suave',
      )}
    >
      {/* O ladrilho existe mesmo sem ícone: numa pilha, uma ação sem ele
          desalinharia o rótulo de todas as outras. */}
      <span className="flex size-4 shrink-0 items-center justify-center">
        {icon && (
          <Icon
            nome={icon}
            size={16}
            className={
              disabled ? 'text-texto-tenue' : perigo ? 'text-erro' : 'text-primaria-texto'
            }
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        {label}
        {disabled && (
          /* O motivo fica NA ação, e não num aviso no rodapé: quem tenta clicar
             está olhando para o botão, não para o pé do painel. */
          <span className="mt-0.5 block text-[0.6875rem] font-normal text-texto-suave">
            {disabled}
          </span>
        )}
      </span>
      {/* A seta promete um passo adiante, então só aparece onde há um — mas o
          lugar dela fica reservado, senão o rótulo muda de largura com o
          estado. */}
      <span className="flex size-4 shrink-0 items-center justify-center">
        <Icon
          nome="seta-direita"
          size={16}
          className={cn('text-texto-tenue', disabled && 'invisible')}
        />
      </span>
    </button>
  );
}
